<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <hero-bar>
      Registru R.E.S.D.P.S.
      <resdps-help-tip :text="registerHelp" position="is-bottom" />
      <template #right>
        <succession-help />
      </template>
    </hero-bar>
    <section class="section is-main-section">
      <succession-tabs />
      <card-component title="Registrul pe an" icon="book-open-variant">
        <div class="level register-toolbar">
          <div class="level-left">
            <div class="level-item">
              <b-field label="Anul">
                <b-select v-model="year">
                  <option v-for="option in years" :key="option" :value="option">
                    {{ option }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="level-item">
              <span class="tag is-light is-medium mb-1"
                >{{ rows.length }} pozitii</span
              >
            </div>
          </div>
          <div class="level-right">
            <div class="level-item">
              <b-button
                icon-left="printer"
                :disabled="rows.length === 0"
                @click="print"
              >
                Tipareste
              </b-button>
            </div>
          </div>
        </div>
        <b-loading :active="isLoading" :is-full-page="false" />
        <div class="table-container">
          <table
            id="resdps-register"
            class="table is-fullwidth is-bordered resdps-register"
          >
            <caption class="has-text-weight-semibold">
              {{
                caption
              }}
            </caption>
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Reg. general / Camera</th>
                <th>Defunct / CNP</th>
                <th>Ultimul domiciliu</th>
                <th>Deces</th>
                <th>Rol / Registrul agricol</th>
                <th>Observatii</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.successionFileId"
                :class="{ 'is-cancelled': row.status === 'Cancelled' }"
                @click="openFile(row.successionFileId)"
              >
                <td>
                  <strong>{{ number(row.number) }}</strong
                  ><br />
                  {{ date(row.registrationDate) }}
                  <span v-if="row.status === 'Cancelled'" class="tag is-danger"
                    >Anulat</span
                  >
                </td>
                <td>
                  {{ pair(row.generalRegistryNumber, row.generalRegistryDate)
                  }}<br />
                  {{
                    pair(
                      row.chamberRegistrationNumber,
                      row.chamberRegistrationDate
                    )
                  }}
                </td>
                <td>
                  {{ deceased(row) }}<br />
                  {{ row.cnp || "—" }}
                </td>
                <td>
                  {{ locality(row.domicileLocality, row.domicileCounty) }}<br />
                  {{ address(row) }}
                </td>
                <td>
                  {{ date(row.dateOfDeath) }}<br />
                  {{ certificate(row) }}<br />
                  {{ locality(row.deathPlaceLocality, row.deathPlaceCounty) }}
                </td>
                <td>
                  {{ row.fiscalRollNumber || "—" }}<br />
                  {{ agricultural(row) }}
                </td>
                <td>{{ row.observations }}</td>
              </tr>
              <tr v-if="!isLoading && rows.length === 0">
                <td colspan="7" class="has-text-centered has-text-grey">
                  Nicio pozitie in acest an
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </card-component>
    </section>
  </div>
</template>

<script>
import printJS from "print-js";
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import { describeError } from "@/helpers/resdpsErrors";
import {
  formatAddress,
  formatAgriculturalRegister,
  formatDate,
  formatDeceased,
  formatLocality,
  formatRegisterNumber,
  registerYears,
} from "@/helpers/resdps";
import { CONCEPT_HELP } from "@/helpers/resdpsHelp";
import TitleBar from "@/components/TitleBar";
import HeroBar from "@/components/HeroBar";
import CardComponent from "@/components/CardComponent";
import ResdpsHelpTip from "@/components/succession/resdps-help-tip.vue";
import SuccessionHelp from "@/components/succession/succession-help.vue";
import SuccessionTabs from "@/components/succession/succession-tabs.vue";

const PRINT_STYLE =
  "@page { size: landscape; } table { border-collapse: collapse; width: 100%; font-size: 10px; } " +
  "caption { font-size: 13px; font-weight: bold; margin-bottom: 6px; } " +
  "th, td { border: 1px solid #999; padding: 3px; vertical-align: top; text-align: left; } " +
  ".is-cancelled td { color: #666; }";

export default {
  name: "ResdpsRegister",
  components: {
    TitleBar,
    HeroBar,
    CardComponent,
    ResdpsHelpTip,
    SuccessionHelp,
    SuccessionTabs,
  },
  data() {
    const today = new Date();
    return {
      registerHelp: CONCEPT_HELP.register.text,
      year: today.getFullYear(),
      years: registerYears(today),
      rows: [],
      isLoading: false,
      titleStack: ["Succesiuni", "Registru R.E.S.D.P.S."],
    };
  },
  computed: {
    caption() {
      return "R.E.S.D.P.S. — anul " + this.year;
    },
  },
  watch: {
    year() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    // Fiecare incarcare scrie ViewedRegister in jurnal (spec B), pentru ca registrul contine CNP-uri.
    async load() {
      this.isLoading = true;
      try {
        this.rows = await SuccessionService.getRegister(this.year);
      } catch (error) {
        this.rows = [];
        notify.error(this, describeError(error).message);
      } finally {
        this.isLoading = false;
      }
    },
    print() {
      printJS({
        printable: "resdps-register",
        type: "html",
        documentTitle: this.caption,
        style: PRINT_STYLE,
        scanStyles: false,
      });
    },
    openFile(id) {
      this.$router.push({ name: "succession.file", params: { id } });
    },
    number: formatRegisterNumber,
    date: formatDate,
    deceased: formatDeceased,
    locality: formatLocality,
    address: formatAddress,
    agricultural: formatAgriculturalRegister,
    pair(number, date) {
      return number ? number + " / " + formatDate(date) : "—";
    },
    certificate(row) {
      return row.deathCertificateNumber
        ? "act " + row.deathCertificateNumber
        : "";
    },
  },
};
</script>

<style scoped>
/* Numarul de pozitii si butonul Tipareste stau pe linia selectorului de an, nu la mijlocul etichetei. */
.register-toolbar {
  align-items: flex-end;
}
.resdps-register tbody tr {
  cursor: pointer;
}
.resdps-register .is-cancelled td {
  color: #7a7a7a;
}
</style>
